/**
 * LARCIN — collection page behaviour.
 *
 * Two jobs, and deliberately nothing else. Filtering, sorting, paging and the
 * cart itself all belong to code that already exists: the theme's facets block
 * drives the URL, {% paginate %} draws the pages, and the theme's own
 * product-form-component performs the add and opens the cart drawer. Anything
 * this file added to those would be a second opinion competing with the first.
 *
 * 1. THE CONFIRMED-ADDED STATE on each card's buy button. The theme sets its
 *    own data-added flag the moment the button is clicked, before the request
 *    has been anywhere, and never reliably takes it off again. A tick driven by
 *    that would be a guess, and a permanent one. So this listens for Shopify's
 *    cart events instead: the tick appears when a line is confirmed, clears on
 *    a cart error, and takes the theme's stale flag with it when it goes.
 *
 * 2. THE FAQ DISCLOSURES, which are <details> elements — the browser already
 *    opens and closes them, so all that is left is swapping the +/− glyph.
 *
 * One delegated listener per concern, all on document, so nothing needs
 * rebinding when the facets block re-renders the grid underneath them.
 */
(() => {
  const ADDED_VISIBLE_MS = 1600;

  /** Button → the timer that will take its tick away again. */
  const addedTimers = new Map();

  /**
   * The buy button belonging to whichever card raised a cart event.
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  function buttonFromEvent(event) {
    const origin = event.target instanceof Element ? event.target : null;
    const card = origin?.closest('.lcp-card');
    return card?.querySelector('.lcp-card__button') ?? null;
  }

  function showAdded(event) {
    const button = buttonFromEvent(event);
    if (!button) return;

    // Restarted, not stacked. A second add while the first tick is still up gets
    // the full display time of its own rather than what was left of the last.
    clearTimeout(addedTimers.get(button));
    button.removeAttribute('data-lv-added');
    // Reading a layout property between the two writes forces a style recalc, so
    // the pop animation runs again instead of being treated as the same,
    // already-finished animation.
    void button.offsetWidth;
    button.setAttribute('data-lv-added', '');

    addedTimers.set(
      button,
      setTimeout(() => {
        button.removeAttribute('data-lv-added');
        button.removeAttribute('data-added');
        addedTimers.delete(button);
      }, ADDED_VISIBLE_MS)
    );
  }

  function clearAdded(event) {
    const button = buttonFromEvent(event);
    if (!button) return;

    clearTimeout(addedTimers.get(button));
    addedTimers.delete(button);
    button.removeAttribute('data-lv-added');
    button.removeAttribute('data-added');
  }

  document.addEventListener('shopify:cart:lines-update', showAdded);
  document.addEventListener('shopify:cart:error', clearAdded);

  /* ----------------------------------------------------------- Search
     Filters the products already on the page as the customer types.

     This is the one place on this page that filters in JavaScript, and it is
     deliberate: it is a name lookup for somebody who already knows what they
     want, not a facet. It never touches the URL, never fights the real filters
     beside it, and it corrects the result count while it runs so the number
     never describes a different set than the one on screen. Clearing it puts
     everything back exactly as the server rendered it.

     It matches the fragrance name and the fragrance it is inspired by — the two
     things a customer arrives with — from text the page put on each card, not
     scraped from whatever the card happens to display today.

     SCOPE IS THE WHOLE STORE, not this collection. Two things make that
     true, and they are separate on purpose:

     1. THE CARDS. The first time a customer focuses or types, this section is
        fetched once as rendered for the store's full product listing
        (Shopify's Section Rendering API) and every card not already on the
        page is added to the grid, hidden. They are the same server-rendered
        cards, buy button included, so a result from outside this collection
        looks and works exactly like one inside it, and they only ever show
        while a search is running.

     2. WHAT MATCHES. The words go to Shopify's predictive search — the same
        endpoint, and so the same results, as the search in the header, which
        is why "creed" finds the two fragrances inspired by Creed and a
        misspelling finds nothing at all. Its order is kept too: the grid is
        reordered by CSS so the best match leads. The local match on the name
        and the inspired-by line stays alongside it and fills in behind, so a
        fragrance beyond Shopify's ten suggestions is still found by name.

     If the search request fails, the local match still answers on its own. */
  document.querySelectorAll('[data-lcp-search]').forEach((box) => {
    const input = box.querySelector('[data-lcp-search-input]');
    const clear = box.querySelector('[data-lcp-search-clear]');
    if (!input) return;

    const section = box.closest('.lcp');
    if (!section) return;

    const items = Array.from(section.querySelectorAll('.lcp-grid__item[data-lcp-text]'));
    const grid = section.querySelector('.lcp-grid');

    const productIdOf = (item) => item.querySelector('[data-product-id]')?.dataset.productId || '';
    const known = new Set(items.map(productIdOf).filter(Boolean));

    /** The handle in a product URL, which is how Shopify's results are matched
     * to the cards on this page. Locale prefixes and ?variant= are ignored.
     * @param {string} href
     */
    const handleFromHref = (href) => href.match(/\/products\/([^/?#]+)/)?.[1] ?? '';

    /** A card's own handle, read once and kept on the element. */
    const handleOf = (item) => {
      if (item.dataset.lcpHandle == null) {
        const link = item.querySelector('a[href*="/products/"]');
        item.dataset.lcpHandle = handleFromHref(link?.getAttribute('href') || '');
      }
      return item.dataset.lcpHandle;
    };

    let catalogueLoad = null;
    const loadCatalogue = () => {
      if (catalogueLoad) return catalogueLoad;
      const source = box.dataset.lcpSearchSource;
      const sectionId = box.dataset.lcpSearchSection;
      if (!source || !sectionId || !grid) return (catalogueLoad = Promise.resolve());

      catalogueLoad = (async () => {
        for (let page = 1; page <= 10; page += 1) {
          const url = new URL(source, window.location.origin);
          url.searchParams.set('section_id', sectionId);
          url.searchParams.set('page', String(page));
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response.ok) break;

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          for (const found of doc.querySelectorAll('.lcp-grid__item[data-lcp-text]')) {
            const id = productIdOf(found);
            if (!id || known.has(id)) continue;
            known.add(id);
            const node = document.importNode(found, true);
            node.hidden = true;
            node.setAttribute('data-lcp-search-extra', '');
            grid.append(node);
            items.push(node);
          }

          if (!doc.querySelector('.lcp-pagination a[rel="next"]')) break;
        }
      })().catch(() => {
        // The page's own products are still searched; nothing else to do.
      });

      return catalogueLoad;
    };
    /* ---- Shopify's own search -------------------------------------
       The predictive search endpoint: the one the header's search reads, so
       the same words give the same products in the same order. One request
       per settled query, then remembered, so backspacing through a word never
       asks twice.

       The results page was tried first and abandoned: on a query it cannot
       match it pads the grid out with the whole catalogue, and "no results"
       would have arrived looking like "everything matched". */
    const STORE_LIMIT = 10;
    const storeAnswers = new Map();
    const storeAsked = new Map();

    /** @returns {Promise<Map<string, number> | null>} handle → place in the results */
    const searchStore = (query) => {
      if (storeAnswers.has(query)) return Promise.resolve(storeAnswers.get(query));
      if (storeAsked.has(query)) return storeAsked.get(query);

      const searchUrl = box.dataset.lcpSearchUrl;
      if (!searchUrl) return Promise.resolve(null);

      const request = (async () => {
        const url = new URL(`${searchUrl}.json`, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', String(STORE_LIMIT));

        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) return null;

        const data = await response.json();
        const products = data?.resources?.results?.products ?? [];
        const ranks = new Map();
        products.forEach((product, index) => {
          if (product?.handle) ranks.set(product.handle, index);
        });
        return ranks;
      })()
        .catch(() => null)
        .then((value) => {
          storeAnswers.set(query, value);
          storeAsked.delete(query);
          return value;
        });

      storeAsked.set(query, request);
      return request;
    };

    const editorial = Array.from(section.querySelectorAll('.lcp-grid__item--editorial'));
    const count = section.querySelector('[data-lcp-count]');
    const empty = section.querySelector('[data-lcp-search-empty]');
    const pagination = section.querySelector('.lcp-pagination');
    if (!items.length) return;

    // Only now does the control appear. A search box that does nothing when
    // typed into is worse than no search box.
    box.hidden = false;
    section.querySelector('[data-lcp-search-note]')?.removeAttribute('hidden');

    const originalCount = count ? count.textContent : '';

    const setCount = (n, filtering) => {
      if (!count) return;
      if (!filtering) {
        count.textContent = originalCount;
        return;
      }
      const one = count.dataset.lcpCountOne || 'product';
      const many = count.dataset.lcpCountMany || 'products';
      count.textContent = `${n} ${n === 1 ? one : many}`;
    };

    /**
     * Draws the result of one query: what the page matched by name and
     * inspiration, plus whatever Shopify's search returned for it.
     *
     * @param {string} query
     * @param {Map<string, number> | null} storeRanks - null while Shopify's
     *   answer is still on its way, or if the request failed.
     */
    const render = (query, storeRanks) => {
      const filtering = query.length > 0;
      // Every word must appear, so word order does not matter.
      const words = query.split(/\s+/).filter(Boolean);

      let shown = 0;
      for (const item of items) {
        const text = item.dataset.lcpText || '';
        // Cards fetched from the wider catalogue belong to searches only; with
        // the box empty the page is exactly what this collection rendered.
        const extra = item.hasAttribute('data-lcp-search-extra');
        const rank = storeRanks ? storeRanks.get(handleOf(item)) : undefined;
        let hit = !extra;
        if (filtering) {
          hit = rank !== undefined || words.every((word) => text.includes(word));
        }
        item.hidden = !hit;
        if (hit) shown += 1;

        /* Shopify's order, without moving anything: the cards are grid items,
           so order alone re-sequences them, and the buy button inside each one
           never leaves the document. Matches Shopify did not rank keep the
           page's own order, behind the ones it did. */
        if (filtering && rank !== undefined) item.style.order = String(rank);
        else if (filtering) item.style.order = String(STORE_LIMIT);
        else item.style.order = '';
      }

      // The editorial tiles are not products and cannot match a name, so they
      // stand down while a search is running rather than sitting in the results
      // as something the customer has to read past.
      for (const tile of editorial) tile.hidden = filtering;

      // Paging through a filtered view would page the unfiltered collection.
      if (pagination) pagination.hidden = filtering;

      if (empty) empty.hidden = !filtering || shown > 0;
      if (clear) clear.hidden = !filtering;
      box.toggleAttribute('data-lcp-searching', filtering);

      setCount(shown, filtering);
    };

    /* The newest query wins. An earlier request that comes back late is
       dropped rather than painting results for words no longer in the box. */
    let latest = 0;

    const apply = async () => {
      const query = input.value.trim().toLowerCase();
      const token = (latest += 1);

      // Straight away, from what is already on the page.
      render(query, storeAnswers.get(query) ?? null);
      if (!query) return;

      // Then the rest of the store, once its cards and Shopify's answer are in.
      await loadCatalogue();
      const handles = await searchStore(query);
      if (token !== latest) return;

      render(query, handles);
    };

    /* The long placeholder names both things you can search by, which is worth
       a lot on a first visit and is also six words too many for a 375px field —
       it was being cut mid-word. Below 600px the shorter one stands in. */
    const longPlaceholder = input.getAttribute('placeholder') || '';
    const shortPlaceholder = input.dataset.lcpPlaceholderShort || longPlaceholder;
    const fitPlaceholder = () => {
      const short = window.matchMedia('(max-width: 599px)').matches;
      input.setAttribute('placeholder', short ? shortPlaceholder : longPlaceholder);
    };
    fitPlaceholder();
    window.matchMedia('(max-width: 599px)').addEventListener('change', fitPlaceholder);

    // Long enough that a word being typed is one search rather than six.
    // apply() waits for the catalogue and for Shopify by itself, so there is
    // nothing to chase here afterwards.
    let timer = null;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(apply, 220);
    });

    // Start fetching on focus, so the full catalogue is usually ready by the
    // time the first word is typed.
    input.addEventListener('focus', () => loadCatalogue(), { once: true });

    // Enter must not submit anything — there is no form behind this and the
    // filtering has already happened.
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') event.preventDefault();
      if (event.key === 'Escape' && input.value) {
        input.value = '';
        apply();
      }
    });

    clear?.addEventListener('click', () => {
      input.value = '';
      apply();
      input.focus();
    });
  });

  /* ----------------------------------------------------------- Trust strip
     On a phone the four claims loop. The second copy is made here, marked
     aria-hidden and inert, so the page HTML holds each claim once and a
     screen reader hears them once. Nothing runs for reduced motion; without
     this script the strip simply scrolls. The CSS only animates on phones. */
  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.querySelectorAll('[data-lcp-trust]').forEach((strip) => {
      if (strip.classList.contains('is-looping')) return;
      const group = strip.querySelector('[data-lcp-trust-group]');
      if (!group || !group.children.length) return;
      const addCopy = () => {
        const copy = group.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        copy.setAttribute('inert', '');
        copy.setAttribute('data-clone', '');
        copy.removeAttribute('data-lcp-trust-group');
        group.parentElement.append(copy);
      };
      addCopy();
      strip.classList.add('is-looping');

      // The strip runs the full width of the page, which on a wide screen is
      // more than two copies of the claims. Enough copies are added to cover
      // the width plus one, and the loop moves by exactly one copy, so there
      // is never a gap at the right-hand end.
      const fill = () => {
        const one = group.getBoundingClientRect().width;
        if (!one) return;
        const needed = Math.ceil(strip.clientWidth / one) + 1;
        let copies = group.parentElement.querySelectorAll('[data-clone]').length;
        while (copies + 1 < needed) {
          addCopy();
          copies += 1;
        }
        strip.style.setProperty('--lcp-trust-shift', `${one}px`);
      };
      fill();
      window.addEventListener('resize', fill, { passive: true });
    });
  }

  /* Sorting submits on change, once. The form ships with a visible Apply
     button and works without any of this; the button is only hidden after the
     upgrade has actually been applied, so a customer whose script failed to
     load is never left with a control that does nothing. */
  document.querySelectorAll('[data-lcp-sort]').forEach((form) => {
    const select = form.querySelector('[data-lcp-sort-select]');
    if (!select) return;

    select.addEventListener('change', () => form.submit());
    form.setAttribute('data-lcp-sort-upgraded', '');
  });

  /* The glyph on a FAQ disclosure. `toggle` fires after the browser has already
     opened or closed the element, so `open` is the state to read, not invert. */
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement)) return;
      if (!details.classList.contains('lcp-faq__item')) return;

      const glyph = details.querySelector('[data-lcp-faq-glyph]');
      if (glyph) glyph.textContent = details.open ? '−' : '+';
    },
    true
  );
})();
